import type { IntrinsicSystemProps, MotionPropsFor } from '@hover/blueprint';
import {
  Motion,
  forwardRef,
  motion,
  system,
  transition,
} from '@hover/blueprint';
import type { Ref } from 'react';
import React from 'react';

import {
  useGenerateImageLoadingState,
  useHomeownerTranslate,
} from 'src/modules/homeownerApp/hooks';

import { altStyle, useImageFocus } from '../../helpers/styles';
import type {
  AiOrSampleGeneratedImage,
  AiOrSampleOriginalImage,
} from '../../types';
import { imageAspectRatio } from '../ImageCard';
import { LoadingState } from '../LoadingState';
import { ImageError, ImageErrorRetry } from '../errors';

export interface ThumbnailProps extends IntrinsicSystemProps<'a'> {
  aiGeneratedImage: AiOrSampleGeneratedImage;
  consumerId: string;
  href?: string;
  imageProps?: MotionPropsFor<IntrinsicSystemProps<'img'>> & {
    ref?: Ref<Element>;
  };
  label?: string;
  orientation?: string | null;
  /**
   * Optionally provide the original image to use as a placeholder while the
   * generated image is generating and loading
   */
  originalImage?: AiOrSampleOriginalImage;
}

export const Thumbnail = forwardRef<ThumbnailProps, 'a'>(
  (
    {
      aiGeneratedImage,
      consumerId,
      href,
      imageProps,
      label,
      orientation: orientationProp,
      originalImage,
      ...props
    },
    ref,
  ) => {
    const { generatedImagePath, thumbnailUrl, style } = aiGeneratedImage;

    const t = useHomeownerTranslate();

    const focus = useImageFocus();

    const {
      imageProps: generatingImageProps,
      imageVariants: { generating, loading, loaded },
      isGeneratedLoading,
      isLoadingImage,
      error,
      retry,
    } = useGenerateImageLoadingState({
      consumerId,
      image: aiGeneratedImage,
    });

    const isInteractive = !error && !!href;
    const Element = isInteractive ? system.a : system.div;

    const orientation = orientationProp ?? originalImage?.orientation;
    const aspectRatio = orientation
      ? String(imageAspectRatio(orientation))
      : undefined;

    return (
      <Element
        aria-label={label}
        backgroundColor="darkAlpha.50"
        border="none"
        borderRadius="400"
        display="block"
        filter="brightness(1)"
        overflow="hidden"
        padding={0}
        position="relative"
        sx={{ ...transition.easing250('filter'), ...focus }}
        {...(isInteractive ? { href } : {})}
        {...props}
        ref={ref}
      >
        <Motion.Soft>
          {error ? (
            <ImageError
              bg="lightAlpha.200"
              color="neutral.700"
              message={t('generatedImageThumbnail.failed', {
                id: aiGeneratedImage.id,
                style,
              })}
              position="relative"
              sx={{ aspectRatio }}
              width="100%"
            >
              <ImageErrorRetry onClick={retry}>
                {t('generatedImageThumbnail.retry')}
              </ImageErrorRetry>
            </ImageError>
          ) : (
            <motion.img
              {...generatingImageProps}
              {...altStyle()}
              alt={label}
              animate={
                isGeneratedLoading || isLoadingImage ? undefined : loaded
              }
              cursor="pointer"
              display="block"
              height="100%"
              initial={isGeneratedLoading ? generating : loading}
              objectFit="cover"
              src={
                thumbnailUrl && thumbnailUrl !== ''
                  ? thumbnailUrl
                  : generatedImagePath
              }
              sx={{ aspectRatio }}
              width="100%"
              {...imageProps}
            />
          )}
        </Motion.Soft>
        <LoadingState
          color="neutral.900"
          height="100%"
          isLoading={isGeneratedLoading && !error}
          label={t('generatedImageThumbnail.loading')}
          width="100%"
        />
      </Element>
    );
  },
);
